<template>
  <div class="content-grid content-grid--subpage content-grid--subpage-table">
    <Breadcrumbs
        :title="t(`routes.admin.children.activities.registrations.add.name`)"
    />
    <div class="content-grid__header">
      <BackLinkTitle
          :title="t(`routes.admin.children.activities.registrations.add.name`)"
      />
      <SearchInput v-if="registrations.length"
                   v-model="registrationsSearchValue"
                   class="util--ml-auto"/>
    </div>
    <Card :title="t('activities.selectAnActivity')">
      <form class="form">
        <FormRow>
          <FormSearchableSelect
              v-model="selectedActivityId"
              :label="t('activities.activity')"
              :options="activitiesOptions"
              :rules="[]"
              name="activityId"
          />

          <FormSearchableSelect
              v-if="selectedActivityId"
              v-model="selectedActivityOccurrenceId"
              :label="t('activities.selectAnOccurrence')"
              :options="activityOccurrencesOptions"
              :rules="[]"
              name="activityOccurrenceId"
          />
        </FormRow>
      </form>
    </Card>
    <Card v-if="selectedActivityOccurrenceId.length" :title="t('registrations.registrations')">
      <ActivityRegistrationTable
          v-if="registrations.length"
          v-model:activity-occurrence-id="selectedActivityOccurrenceId"
          v-model:registrations="registrations"
          v-model:registrationsAreLoading="registrationsAreLoading"
          v-model:registrationsSearchValue="registrationsSearchValue"
          @registration-deleted="fetchActivityRegistrations"
      />
      <p v-else>{{ t('registrations.emptyRegistrations') }}</p>
    </Card>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import Card from '@/components/layout/Card.vue';
import SearchInput from "@/components/layout/SearchInput.vue";
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue";
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue";
import FormRow from "@/components/forms/FormRow.vue";
import ActivityRegistrationTable from "@/components/tables/ActivityRegistrationTable.vue";
import {useActivityOccurrenceService, useActivityService} from "@/inversify.config";
import {Activity, ActivityRegistration} from "@/types/entities/activity";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import {ComputedRef} from "vue/dist/vue";
import {FormOption} from "@/types";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  activityId: string
}>()

const {t} = useI18n();

const activityService = useActivityService();
const activityOccurrenceService = useActivityOccurrenceService()

const selectedActivityId = ref(props.activityId ?? "")
const activities = ref<Activity[]>(await activityService.getAllActivities() ?? [])
const selectedActivity = ref<Activity>(findActivityWithId(selectedActivityId.value))

const activityOccurrences = ref<ActivityOccurrence[]>(selectedActivity.value ? selectedActivity.value.activityOccurrences : [])
const selectedActivityOccurrence = ref<ActivityOccurrence>(activityOccurrences.value.length > 0 ? activityOccurrences.value[0] as ActivityOccurrence : new ActivityOccurrence())
const selectedActivityOccurrenceId = ref(selectedActivityOccurrence.value.id ?? "")

const registrations = ref<ActivityRegistration[]>([]);
const registrationsAreLoading = ref(false);
const registrationsSearchValue = ref("");

onMounted(async () => {
  await fetchActivityRegistrations()
})

let activitiesOptions: ComputedRef<FormOption[]> = computed(() => {
  let formOptions = activities.value
      .filter(x => x && x.id && x.nameFr)
      .map(({id, nameFr}) => ({
        name: id,
        label: nameFr
      })) as FormOption[]
  return formOptions.sortByLabel()
});

let activityOccurrencesOptions: any = computed(() => {
  if (activityOccurrences.value.length === 0)
    return []
  return activityOccurrences.value
      .filter(x => x && x.id && x.startDate)
      .map(x => {
        let startDate = x.startDate ? x.startDate : ""
        let endDate = x.endDate ? ` ---> ${x.endDate}` : ''
        return {
          name: x.id,
          label: `${startDate}${endDate}`
        }
      });
});

watch(selectedActivityId, (newId) => {
  registrations.value = []
  selectedActivity.value = findActivityWithId(newId)

  activityOccurrences.value = selectedActivity.value.activityOccurrences
  if (activityOccurrences.value.length === 0)
    return
  selectedActivityOccurrence.value = activityOccurrences.value[0] as ActivityOccurrence
  selectedActivityOccurrenceId.value = activityOccurrences.value[0].id as string
})

watch(selectedActivityOccurrenceId, async () => {
  await fetchActivityRegistrations()
})

async function fetchActivityRegistrations() {
  registrationsAreLoading.value = true
  registrations.value = await activityOccurrenceService.getActivityOccurrenceRegistrations(selectedActivityOccurrenceId.value);
  registrationsAreLoading.value = false
}

function findActivityWithId(id: string): Activity {
  return activities.value.find(x => x.id == id) as Activity;
}
</script>
